import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import StatusBadge, { UrgencyFlag } from "@/components/StatusBadge";
import { StatGridSkeleton, CardSkeleton } from "@/components/Skeletons";
import PromoBanner from "@/components/PromoBanner";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import { DollarSign, CheckCircle2, FileText, Calendar, Plus, UserPlus, Wrench, ClipboardList } from "lucide-react";
import { fmtTime } from "@/lib/format";
import { Button } from "@/components/ui/button";

function StatCard({ label, value, hint, icon: Icon, color, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">{label}</div>
          <div className="text-2xl sm:text-3xl font-bold mt-1.5" style={{ fontFamily: "Outfit" }}>{value}</div>
          {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
        </div>
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayJobs, setTodayJobs] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, j] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/jobs", { params: { today: true } }),
      ]);
      setStats(s.data);
      setTodayJobs(j.data);
    })();
  }, []);

  return (
    <div className="space-y-6">
      <PromoBanner />

      <div>
        <h1 className="text-3xl font-bold" data-testid="dashboard-greeting">
          Hi, {user?.full_name?.split(" ")[0] || "there"} 👋
        </h1>
        <p className="text-slate-500 mt-1">Here's what's happening today.</p>
      </div>

      <OnboardingChecklist />

      {stats === null ? (
        <StatGridSkeleton />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard
            testId="stat-revenue"
            label="Revenue This Week"
            value={`$${(stats.revenue_week || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            hint={stats.revenue_week === 0 ? "updates as invoices are paid" : null}
            icon={DollarSign}
            color="bg-green-100 text-green-700"
          />
          <StatCard
            testId="stat-completed"
            label="Jobs Completed"
            value={stats.jobs_completed || 0}
            hint={stats.jobs_completed === 0 ? "updates as you finish jobs" : null}
            icon={CheckCircle2}
            color="bg-blue-100 text-blue-700"
          />
          <StatCard
            testId="stat-outstanding"
            label="Outstanding Invoices"
            value={`$${(stats.outstanding_invoices?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            hint={`${stats.outstanding_invoices?.count || 0} unpaid`}
            icon={FileText}
            color="bg-orange-100 text-orange-700"
          />
          <StatCard
            testId="stat-scheduled"
            label="Jobs Scheduled"
            value={stats.jobs_scheduled || 0}
            hint={stats.jobs_scheduled === 0 ? "updates as you add jobs" : null}
            icon={Calendar}
            color="bg-slate-100 text-slate-700"
          />
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3">
        <Link to="/jobs/new" data-testid="quick-new-job">
          <Button className="w-full h-16 bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold rounded-2xl shadow-sm justify-start gap-3 px-5">
            <div className="h-9 w-9 rounded-lg bg-white/20 flex items-center justify-center"><Plus className="w-5 h-5" /></div>
            New Job
          </Button>
        </Link>
        <Link to="/estimates/new" data-testid="quick-new-estimate">
          <Button className="w-full h-16 bg-white hover:bg-slate-50 text-slate-900 border-2 border-slate-200 text-base font-semibold rounded-2xl shadow-sm justify-start gap-3 px-5">
            <div className="h-9 w-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center"><ClipboardList className="w-5 h-5" /></div>
            New Estimate
          </Button>
        </Link>
        <Link to="/customers?new=1" data-testid="quick-new-customer">
          <Button className="w-full h-16 bg-white hover:bg-slate-50 text-slate-900 border-2 border-slate-200 text-base font-semibold rounded-2xl shadow-sm justify-start gap-3 px-5">
            <div className="h-9 w-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center"><UserPlus className="w-5 h-5" /></div>
            New Customer
          </Button>
        </Link>
      </div>

      <div>
        <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "Outfit" }}>Today's Jobs</h2>
        {todayJobs === null ? (
          <CardSkeleton count={2} />
        ) : todayJobs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center" data-testid="no-today-jobs">
            <Wrench className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-500">Nothing on the schedule today.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {todayJobs.map((j) => (
              <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`today-job-${j.id}`}>
                <div className={`bg-white rounded-2xl border p-4 hover:shadow-sm transition-all active:scale-[0.99] mb-2 ${j.urgency === "emergency" ? "border-red-300 ring-2 ring-red-100" : "border-slate-200 hover:border-blue-300"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <UrgencyFlag urgency={j.urgency} />
                        <div className="font-semibold text-slate-900 truncate">{j.title}</div>
                      </div>
                      <div className="text-sm text-slate-500 mt-1 truncate">
                        {j.customer_name} • {j.scheduled_time ? fmtTime(j.scheduled_time) : "any time"} • {j.tech_name}
                      </div>
                    </div>
                    <StatusBadge status={j.status} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
